import {
	APP_DATE_FORMAT,
	APP_DATE_TIME_FORMAT,
	APP_TIME_FORMAT,
} from "@/shared/lib/dateTimeFormats";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

/** API dates are typically UTC ISO strings. Parse as UTC and format in target timezone. */
export function formatDateTime(
	date: string | Date | null | undefined,
	tz: string,
): string {
	if (!date) return "—";
	return dayjs
		.utc(date)
		.tz(tz || "UTC")
		.format(APP_DATE_TIME_FORMAT);
}

export function formatDate(
	date: string | Date | null | undefined,
	tz: string,
): string {
	if (!date) return "—";
	return dayjs
		.utc(date)
		.tz(tz || "UTC")
		.format(APP_DATE_FORMAT);
}

/** Format date-only "YYYY-MM-DD" string in timezone (for calendar range display) */
export function formatDateRange(dateStr: string, tz: string): string {
	if (!dateStr) return "—";
	return dayjs.tz(dateStr, "YYYY-MM-DD", tz || "UTC").format(APP_DATE_FORMAT);
}

export function formatTime(
	date: string | Date | null | undefined,
	tz: string,
): string {
	if (!date) return "—";
	return dayjs
		.utc(date)
		.tz(tz || "UTC")
		.format(APP_TIME_FORMAT);
}

export function formatDateTimeCompact(
	date: string | Date | null | undefined,
	tz: string,
): string {
	if (!date) return "—";
	return dayjs
		.utc(date)
		.tz(tz || "UTC")
		.format(APP_DATE_TIME_FORMAT);
}

export function formatDateTimeSession(
	date: string | Date | null | undefined,
	tz: string,
): string {
	if (!date) return "—";
	return dayjs
		.utc(date)
		.tz(tz || "UTC")
		.format(APP_DATE_TIME_FORMAT);
}

/** Parse UTC date for comparisons (returns dayjs in UTC) */
export function parseUtc(date: string | Date | null | undefined): dayjs.Dayjs {
	return dayjs.utc(date ?? 0);
}

/** Get "now" in a specific timezone for day-boundary comparisons */
export function nowInTz(tz: string): dayjs.Dayjs {
	return dayjs().tz(tz);
}

/**
 * Convert UTC API date to "YYYY-MM-DDTHH:mm" for datetime-local input value.
 * The input displays times in the target timezone.
 */
export function toDateTimeLocalValue(
	date: string | Date | null | undefined,
	tz: string,
): string {
	if (!date) return "";
	return dayjs
		.utc(date)
		.tz(tz || "UTC")
		.format("YYYY-MM-DDTHH:mm");
}

/**
 * Parse "YYYY-MM-DDTHH:mm" from datetime-local input as being in target timezone,
 * and return a Date for API submission (stored as UTC).
 */
export function fromDateTimeLocalValue(value: string, tz: string): Date {
	if (!value) return new Date(0);
	return dayjs
		.tz(value, "YYYY-MM-DDTHH:mm", tz || "UTC")
		.utc()
		.toDate();
}
